/**
 * 操作履歴（監査ログ）
 * 会社の管理サイト・船員アプリで行われた更新操作（誰が・いつ・何を）を一覧する。
 * 「昨日から○○ができない」といった問い合わせのとき、前日の乗下船・役職・権限の変更を
 * ここで追えるようにする。正規の管理者のみ閲覧可。
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { History, RefreshCw, Search } from 'lucide-react';
import * as api from '../services/api';

interface Row {
  id: string; user_id: string | null; user_name: string | null; user_role: string | null; app_type: string | null;
  method: string; path: string; action: string; status: number | null; summary: string | null; ip: string | null; created_at: string;
}

const ROLE_LABEL: Record<string, string> = { system_admin: 'システム管理者', shore_admin: '陸上管理者', captain: '船長', crew: '船員' };
const APP_LABEL: Record<string, string> = { admin: '管理サイト', crew: '船員アプリ', personal: '個人アプリ' };

function fmt(ts: string): string {
  // SQLite の datetime('now') は UTC。JST で表示する
  const d = new Date(ts.includes('T') ? ts : ts.replace(' ', 'T') + 'Z');
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

const todayYmd = () => new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 10);
const daysAgoYmd = (n: number) => new Date(Date.now() + 9 * 3600_000 - n * 86400_000).toISOString().slice(0, 10);

export default function AuditLogsPage() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState(daysAgoYmd(7));
  const [to, setTo] = useState(todayYmd());
  const [q, setQ] = useState('');
  const [action, setAction] = useState('');
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get<{ data: Row[] }>('/audit-logs', { limit: '500', from, to, q, action });
      setRows(r?.data ?? []);
    } catch (e: any) { alert(e?.message ?? '操作履歴の取得に失敗しました'); }
    setLoading(false);
  }, [from, to, q, action]);

  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const actions = useMemo(() => Array.from(new Set(rows.map(r => r.action))).sort(), [rows]);

  const inp: React.CSSProperties = { padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff' };

  return (
    <div className="page-wrap">
      <div className="top-bar">
        <div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><History size={20} />操作履歴</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
            管理サイト・船員アプリで行われた登録・変更の記録です。「いつから使えなくなったか」を調べるときは、その前日の乗下船・役職・権限の変更を確認してください。
          </p>
        </div>
      </div>

      <div className="card" style={{ padding: '12px 16px', marginBottom: 12, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div><div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 3 }}>期間（から）</div><input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inp} /></div>
        <div><div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 3 }}>期間（まで）</div><input type="date" value={to} onChange={e => setTo(e.target.value)} style={inp} /></div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 3 }}>操作の種類</div>
          <select value={action} onChange={e => setAction(e.target.value)} style={{ ...inp, minWidth: 200 }}>
            <option value="">すべて</option>
            <option value="自己乗船">自己乗船（船員アプリ）</option>
            <option value="自己下船">自己下船（船員アプリ）</option>
            <option value="配乗">配乗の登録・変更・発令</option>
            <option value="役職">役職・役職権限の変更</option>
            <option value="ユーザー">ユーザーの追加・更新</option>
            <option value="船舶">船舶の追加・更新</option>
            <option value="書類">書類の作成・配信・提出</option>
            {actions.filter(a => !['自己乗船', '自己下船', '配乗', '役職', 'ユーザー', '船舶', '書類'].some(k => a.includes(k))).map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 3 }}>キーワード（名前・内容）</div>
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') load(); }} placeholder="例: 平田、紺星丸、船長" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} />
        </div>
        <button className="btn-p" onClick={load} disabled={loading} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {loading ? <RefreshCw size={14} className="spin" /> : <Search size={14} />}検索
        </button>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr style={{ background: '#f8fafc', color: '#64748b', fontSize: 11.5 }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', whiteSpace: 'nowrap' }}>日時</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', whiteSpace: 'nowrap' }}>操作した人</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', whiteSpace: 'nowrap' }}>どこから</th>
              <th style={{ padding: '10px 12px', textAlign: 'left' }}>操作</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', whiteSpace: 'nowrap' }}>結果</th>
              <th style={{ padding: '10px 12px', textAlign: 'left' }}>内容</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={6} style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>該当する操作はありません</td></tr>
            )}
            {rows.map(r => {
              const ok = (r.status ?? 0) < 400;
              const isOpen = open === r.id;
              return (
                <tr key={r.id} style={{ borderTop: '1px solid #f1f5f9', cursor: r.summary ? 'pointer' : 'default' }} onClick={() => setOpen(isOpen ? null : r.id)}>
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap', color: '#334155' }}>{fmt(r.created_at)}</td>
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                    <span style={{ fontWeight: 700, color: '#0f172a' }}>{r.user_name ?? '—'}</span>
                    {r.user_role && <span style={{ marginLeft: 6, fontSize: 11, color: '#64748b' }}>{ROLE_LABEL[r.user_role] ?? r.user_role}</span>}
                  </td>
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap', color: '#475569' }}>{APP_LABEL[r.app_type ?? ''] ?? (r.app_type ?? '—')}</td>
                  <td style={{ padding: '9px 12px', fontWeight: 700, color: '#1e293b' }}>{r.action}</td>
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: ok ? '#f0fdf4' : '#fef2f2', color: ok ? '#15803d' : '#b91c1c', border: `1px solid ${ok ? '#bbf7d0' : '#fecaca'}` }}>
                      {ok ? '成功' : `失敗 (${r.status})`}
                    </span>
                  </td>
                  <td style={{ padding: '9px 12px', color: '#475569', maxWidth: 520 }}>
                    <div style={{ whiteSpace: isOpen ? 'pre-wrap' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: isOpen ? 'monospace' : undefined, fontSize: isOpen ? 11.5 : undefined }}>
                      {r.summary ?? <span style={{ color: '#cbd5e1' }}>—</span>}
                    </div>
                    {isOpen && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{r.method} {r.path}{r.ip ? ` / ${r.ip}` : ''}</div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
